import React, { useEffect, useState } from 'react';
import { Building2, Plus, Search, Trash2, X, Users } from 'lucide-react';
import { API_BASE, authFetch } from '../../constants';

function CompanyManagerModal({ onClose }) {
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [companyNotifications, setCompanyNotifications] = useState({}); // Track companies with inactive users
  const [viewedCompanies, setViewedCompanies] = useState(new Set()); // Track which companies have been viewed

  useEffect(() => {
    fetchAllCompanies();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredCompanies(companies);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = companies.filter(
        (c) =>
          c.company_name.toLowerCase().includes(term) ||
          c.contact_person?.toLowerCase().includes(term)
      );
      setFilteredCompanies(filtered);
    }
  }, [searchTerm, companies]);

  const fetchAllCompanies = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/companies/`);
      const data = await res.json();
      setCompanies(data);
      setFilteredCompanies(data);
      
      // Check each company for inactive users
      const notifications = {};
      for (const company of data) {
        try {
          const usersRes = await authFetch(`${API_BASE}/auth/users/company/${company.id}`);
          if (usersRes.ok) {
            const users = await usersRes.json();
            const hasInactiveUsers = users.some(user => !user.is_active);
            if (hasInactiveUsers) {
              notifications[company.id] = true;
              console.log(`Company ${company.company_name} (ID: ${company.id}) has inactive users`);
            }
          }
        } catch (err) {
          console.error(`Failed to fetch users for company ${company.id}:`, err);
        }
      }
      console.log('Company notifications:', notifications);
      setCompanyNotifications(notifications);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setSelectedCompany(null);
    setShowAddForm(true);
  };

  const handleSelectCompany = (company) => {
    setSelectedCompany(company);
    setShowAddForm(true);
    // Mark company as viewed
    setViewedCompanies(prev => new Set([...prev, company.id]));
  };

  const handleCloseForm = () => {
    setShowAddForm(false);
    setSelectedCompany(null);
    fetchAllCompanies();
  };

  const handleDeleteCompany = async (companyId, companyName, event) => {
    event.stopPropagation();
    
    if (companyName === 'KAS Internal') {
      alert('Cannot delete KAS Internal company');
      return;
    }

    if (!window.confirm(`Delete company "${companyName}"? This will also delete all associated growers, farms, fields, batches, and samples. This action cannot be undone.`)) {
      return;
    }

    try {
      const res = await authFetch(`${API_BASE}/companies/${companyId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        alert('Company deleted successfully');
        fetchAllCompanies();
        if (selectedCompany?.id === companyId) {
          setSelectedCompany(null);
          setShowAddForm(false);
        }
      } else {
        const error = await res.json();
        alert(error.detail || 'Failed to delete company');
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full h-[90vh] flex flex-col">
        <div className="border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">Company Manager</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 flex overflow-hidden">
          <div className="w-80 border-r flex flex-col bg-gray-50">
            <div className="p-4 space-y-3 border-b bg-white">
              <button
                onClick={handleAddNew}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
              >
                <Plus size={18} />
                Add Company
              </button>

              <div className="relative">
                <Search
                  size={18}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search companies..."
                  className="w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-gray-500">Loading companies...</div>
              ) : filteredCompanies.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  {searchTerm ? 'No companies found' : 'No companies yet. Click "Add Company" to get started.'}
                </div>
              ) : (
                <div className="divide-y">
                  {filteredCompanies.map((company) => (
                    <div
                      key={company.id}
                      onClick={() => handleSelectCompany(company)}
                      className={`p-4 cursor-pointer hover:bg-white transition group ${
                        selectedCompany?.id === company.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 flex items-center gap-2">
                            {company.company_name}
                            {companyNotifications[company.id] && !viewedCompanies.has(company.id) && (
                              <span className="inline-flex items-center justify-center w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                                !
                              </span>
                            )}
                          </div>
                          {company.contact_person && (
                            <div className="text-sm text-gray-600 mt-1">{company.contact_person}</div>
                          )}
                          {company.is_outside_us && (
                            <span className="inline-block mt-1 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                              Outside US
                            </span>
                          )}
                        </div>
                        {company.company_name !== 'KAS Internal' && (
                          <button
                            onClick={(e) => handleDeleteCompany(company.id, company.company_name, e)}
                            className="opacity-0 group-hover:opacity-100 transition ml-2 text-red-500 hover:text-red-700"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 flex items-center justify-center bg-gray-50 overflow-hidden">
            {!showAddForm ? (
              <div className="text-center text-gray-500 p-8">
                <Building2 size={48} className="mx-auto mb-4 text-gray-400" />
                <p className="text-lg">Select a company to view details</p>
                <p className="text-sm mt-2">or click "Add Company" to create a new one</p>
              </div>
            ) : (
              <CompanyForm 
                company={selectedCompany} 
                onClose={handleCloseForm} 
                onSave={handleCloseForm}
                onNotificationUpdate={(companyId, hasInactive) => {
                  setCompanyNotifications(prev => ({
                    ...prev,
                    [companyId]: hasInactive
                  }));
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CompanyForm({ company, onClose, onSave, onNotificationUpdate }) {
  const [companyName, setCompanyName] = useState(company?.company_name || '');
  const [contactPerson, setContactPerson] = useState(company?.contact_person || '');
  const [email, setEmail] = useState(company?.email || '');
  const [phone, setPhone] = useState(company?.phone || '');
  const [address, setAddress] = useState(company?.address || '');
  const [city, setCity] = useState(company?.city || '');
  const [state, setState] = useState(company?.state || '');
  const [zip, setZip] = useState(company?.zip || '');
  const [country, setCountry] = useState(company?.country || 'USA');
  const [notes, setNotes] = useState(company?.notes || '');
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserData, setNewUserData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    role: 'company_user'
  });

  // Fetch users for this company
  useEffect(() => {
    if (company?.id) {
      fetchCompanyUsers();
    } else {
      setUsers([]);
    }
  }, [company?.id]);

  const fetchCompanyUsers = async () => {
    if (!company?.id) return;
    
    setLoadingUsers(true);
    try {
      const res = await authFetch(`${API_BASE}/auth/users/company/${company.id}`);
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const toggleUserActive = async (userId) => {
    // Find the user to check if it's the primary admin
    const userToToggle = users.find(u => u.id === userId);
    
    // Prevent deactivating the primary admin user
    if (userToToggle?.username === 'admin' && userToToggle?.role === 'admin' && userToToggle?.is_active) {
      alert('❌ Cannot deactivate the primary admin user. This would lock you out of the system!');
      return;
    }
    
    try {
      const res = await authFetch(`${API_BASE}/auth/users/${userId}/toggle-active`, {
        method: 'PATCH'
      });
      
      if (res.ok) {
        // Refresh users list
        await fetchCompanyUsers();
        
        // Update notification status for this company
        if (company?.id && onNotificationUpdate) {
          const usersRes = await authFetch(`${API_BASE}/auth/users/company/${company.id}`);
          if (usersRes.ok) {
            const users = await usersRes.json();
            const hasInactiveUsers = users.some(user => !user.is_active);
            onNotificationUpdate(company.id, hasInactiveUsers);
          }
        }
      } else {
        alert('Failed to toggle user status');
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleAddUser = async () => {
    // Validate required fields
    if (!newUserData.username || !newUserData.email || !newUserData.password) {
      alert('Please fill in username, email, and password');
      return;
    }

    try {
      const payload = {
        ...newUserData,
        company_id: company.id
      };

      const res = await authFetch(`${API_BASE}/auth/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        alert('✅ User created successfully! They can now log in.');
        setShowAddUser(false);
        setNewUserData({
          username: '',
          email: '',
          password: '',
          full_name: '',
          role: 'company_user'
        });
        await fetchCompanyUsers();
      } else {
        const error = await res.json();
        alert(`Failed to create user: ${error.detail || 'Unknown error'}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  // Update form fields when company changes
  useEffect(() => {
    setCompanyName(company?.company_name || '');
    setContactPerson(company?.contact_person || '');
    setEmail(company?.email || '');
    setPhone(company?.phone || '');
    setAddress(company?.address || '');
    setCity(company?.city || '');
    setState(company?.state || '');
    setZip(company?.zip || '');
    setCountry(company?.country || 'USA');
    setNotes(company?.notes || '');
  }, [company]);

  const handleSave = async () => {
    if (!companyName.trim()) {
      alert('Please fill in Company Name');
      return;
    }

    const payload = {
      company_name: companyName,
      contact_person: contactPerson,
      email,
      phone,
      address,
      city,
      state,
      zip,
      country,
      notes
    };

    try {
      if (company) {
        // Update existing company
        const res = await authFetch(`${API_BASE}/companies/${company.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (res.ok) {
          alert('Company updated successfully!');
          onSave();
        } else {
          const error = await res.json();
          alert(error.detail || 'Failed to update company');
        }
      } else {
        // Create new company
        const res = await authFetch(`${API_BASE}/companies/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (res.ok) {
          alert('Company added successfully!');
          onSave();
        } else {
          const error = await res.json();
          alert(error.detail || 'Failed to add company');
        }
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const isOutsideUS = country !== 'USA';

  return (
    <div className="w-full h-full flex flex-col p-6">
      <div className="flex-1 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-sm border p-6 max-w-2xl mx-auto">
        <h3 className="text-lg font-semibold mb-4">
          {company ? 'Edit Company' : 'Add New Company'}
        </h3>

        {company?.company_name === 'KAS Internal' && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            <strong>Admin Company:</strong> This is your internal company account.
          </div>
        )}

        <div className="space-y-4">
          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="ABC Corporation"
            />
          </div>

          {/* Contact Person */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contact Person
            </label>
            <input
              type="text"
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="John Doe"
            />
          </div>

          {/* Email & Phone */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="contact@company.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="123 Main St"
            />
          </div>

          {/* City, State, Zip */}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="City"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="CA"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Zip Code
              </label>
              <input
                type="text"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="12345"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country
              </label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="USA">USA</option>
                <option value="Canada">Canada</option>
                <option value="Mexico">Mexico</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          {/* Outside US indicator */}
          {isOutsideUS && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> This company will be marked as outside the US.
              </p>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Additional notes..."
            />
          </div>

          {/* Users Section */}
          {company && (
            <div className="mt-6 pt-6 border-t">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-semibold text-gray-900 flex items-center gap-2">
                  <Users size={18} />
                  Company Users {users.length > 0 && `(${users.length})`}
                </h4>
                <button
                  onClick={() => setShowAddUser(!showAddUser)}
                  className="flex items-center gap-1 text-sm bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 transition"
                >
                  <Plus size={16} />
                  Add User
                </button>
              </div>

              {/* Add User Form */}
              {showAddUser && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h5 className="font-medium text-gray-900 mb-3">Create New User</h5>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Username <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={newUserData.username}
                          onChange={(e) => setNewUserData({...newUserData, username: e.target.value})}
                          className="w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="username"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Full Name
                        </label>
                        <input
                          type="text"
                          value={newUserData.full_name}
                          onChange={(e) => setNewUserData({...newUserData, full_name: e.target.value})}
                          className="w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="John Doe"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={newUserData.email}
                        onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                        className="w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="user@company.com"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Password <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="password"
                          value={newUserData.password}
                          onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                          className="w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="••••••••"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Role
                        </label>
                        <select
                          value={newUserData.role}
                          onChange={(e) => setNewUserData({...newUserData, role: e.target.value})}
                          className="w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="company_user">Company User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={handleAddUser}
                        className="flex-1 bg-blue-600 text-white px-3 py-1.5 text-sm rounded hover:bg-blue-700 transition"
                      >
                        Create User
                      </button>
                      <button
                        onClick={() => {
                          setShowAddUser(false);
                          setNewUserData({
                            username: '',
                            email: '',
                            password: '',
                            full_name: '',
                            role: 'company_user'
                          });
                        }}
                        className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50 transition"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {loadingUsers ? (
                <div className="text-center py-4 text-gray-500">Loading users...</div>
              ) : users.length === 0 ? (
                <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                  No users yet. Click "Add User" to create one.
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {users.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="font-medium text-gray-900">{user.full_name || user.username}</div>
                          {!user.is_active && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                              Inactive
                            </span>
                          )}
                          {user.role === 'admin' && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                              Admin
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600">{user.email}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          Username: {user.username}
                          {user.last_login && (
                            <span className="ml-2">
                              • Last login: {new Date(user.last_login).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      {user.username === 'admin' && user.role === 'admin' && user.is_active ? (
                        <div className="px-3 py-1 text-xs text-gray-500 italic">
                          Protected
                        </div>
                      ) : (
                        <button
                          onClick={() => toggleUserActive(user.id)}
                          className={`px-3 py-1 rounded text-sm font-medium transition ${
                            user.is_active
                              ? 'bg-red-100 text-red-700 hover:bg-red-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          {user.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                <strong>Admin Workflow:</strong> Create user accounts here for your clients. Users will be immediately active and can log in with their credentials. You can deactivate users at any time if needed.
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6 pt-4 border-t">
          <button
            onClick={handleSave}
            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition font-medium"
          >
            {company ? 'Update Company' : 'Add Company'}
          </button>
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            Cancel
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}

export default CompanyManagerModal;
